import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react';
import { useAdminWebSocket } from '../hooks/useAdminWebSocket';
import { useAuth } from './AuthContext';

const AdminWsContext = createContext(null);

export function AdminWsProvider({ token, relayUrl, children }) {
  const listenersRef = useRef(new Set());
  const [connected, setConnected] = useState(false);
  const { logout } = useAuth();

  const dispatch = useCallback((msg) => {
    if (msg.event === 'admin_registered') setConnected(true);
    // Сервер отозвал нашу сессию (revoke в /sessions): сразу гасим UI, не дожидаясь 401
    // от следующего HTTP-запроса. Иначе пользователь продолжит видеть «живой» дашборд
    // ещё несколько секунд после того, как его реально выкинули.
    if (msg.event === 'session_revoked') {
      logout();
      return;
    }
    listenersRef.current.forEach(fn => fn(msg));
  }, [logout]);

  const handleDisconnect = useCallback(() => setConnected(false), []);

  useAdminWebSocket(token, relayUrl, dispatch, handleDisconnect);

  const subscribe = useCallback((fn) => {
    listenersRef.current.add(fn);
    return () => listenersRef.current.delete(fn);
  }, []);

  return (
    <AdminWsContext.Provider value={{ subscribe, connected }}>
      {children}
    </AdminWsContext.Provider>
  );
}

export function useAdminWs(handler) {
  const ctx = useContext(AdminWsContext);
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!ctx?.subscribe) return;
    return ctx.subscribe((msg) => handlerRef.current(msg));
  }, [ctx?.subscribe]); // eslint-disable-line
}

export function useAdminWsConnected() {
  return useContext(AdminWsContext)?.connected ?? false;
}
